import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  BarChart3, 
  Users, 
  CreditCard, 
  TrendingUp,
  Activity,
  Calendar,
  DollarSign,
  UserCheck,
  Crown,
  Star,
  User,
  Shield
} from "lucide-react";
import { useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { isUnauthorizedError } from "@/lib/authUtils";

export default function Dashboard() {
  const { user, isAuthenticated, isLoading } = useAuth();
  const { toast } = useToast();

  // Redirect to home if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      toast({
        title: "Unauthorized",
        description: "You are logged out. Logging in again...",
        variant: "destructive",
      });
      setTimeout(() => {
        window.location.href = "/api/login";
      }, 500);
      return;
    }
  }, [isAuthenticated, isLoading, toast]);

  // Fetch user's subscription with error handling
  const { data: subscription, isLoading: subscriptionLoading, error: subscriptionError } = useQuery({
    queryKey: ['/api/subscription'],
    retry: false,
    enabled: isAuthenticated,
  });

  // Handle subscription error
  useEffect(() => {
    if (subscriptionError && isUnauthorizedError(subscriptionError as Error)) {
      toast({
        title: "Unauthorized",
        description: "You are logged out. Logging in again...",
        variant: "destructive",
      });
      setTimeout(() => {
        window.location.href = "/api/login";
      }, 500);
    }
  }, [subscriptionError, toast]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-8 w-48 mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <Card key={i}>
                <CardContent className="p-6">
                  <Skeleton className="h-12 w-12 rounded-lg mb-4" />
                  <Skeleton className="h-4 w-24 mb-2" />
                  <Skeleton className="h-8 w-16" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return null;
  }

  const getSubscriptionStatus = () => {
    if (subscriptionLoading) return 'Loading...';
    if (!subscription) return 'Free Plan';
    return `${(subscription as any).planType || 'Premium'} (${(subscription as any).status})`;
  };

  const getSubscriptionColor = () => {
    if (!subscription) return 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200';
    switch ((subscription as any).status) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'trialing':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'past_due':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'canceled':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200';
    }
  };

  const getDaysUntilRenewal = () => {
    if (!subscription || !(subscription as any).currentPeriodEnd) return null;
    const now = new Date();
    const endDate = new Date((subscription as any).currentPeriodEnd);
    const diffTime = endDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin':
        return <Crown className="w-5 h-5 text-red-500" />;
      case 'premium':
        return <Star className="w-5 h-5 text-yellow-500" />;
      default:
        return <User className="w-5 h-5 text-blue-500" />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
                Dashboard
              </h1>
              <p className="text-slate-600 dark:text-slate-300">
                Welcome to your personal dashboard, {user.firstName || 'User'}
              </p>
            </div>
            
            {/* Premium Status Badge */}
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2">
                {getRoleIcon(user.role)}
                <Badge 
                  className={`px-3 py-1 ${user.role === 'premium' 
                    ? 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white border-0' 
                    : user.role === 'admin'
                    ? 'bg-gradient-to-r from-red-500 to-pink-600 text-white border-0'
                    : 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200'
                  }`}
                >
                  {user.role === 'premium' ? '⭐ PREMIUM' : 
                   user.role === 'admin' ? '👑 ADMIN' : 
                   '👤 USER'}
                </Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Account Status */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                  <UserCheck className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-300">Account Status</p>
                  <div className="flex items-center mt-1">
                    <Badge className={user.isActive ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'}>
                      {user.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Subscription */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded-lg">
                  <CreditCard className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-300">Subscription</p>
                  <div className="flex items-center mt-1">
                    <Badge className={getSubscriptionColor()}>
                      {getSubscriptionStatus()}
                    </Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Days Until Renewal */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded-lg">
                  <Calendar className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-300">Next Billing</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {getDaysUntilRenewal() !== null ? `${getDaysUntilRenewal()} days` : 'N/A'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Member Since */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
                  <Activity className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-300">Member Since</p>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Account Information */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <BarChart3 className="w-5 h-5 mr-2" />
                  Account Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-600 dark:text-slate-300">Email Address</label>
                      <p className="text-slate-900 dark:text-white mt-1">{user.email || 'Not provided'}</p>
                      <div className="mt-1">
                        <Badge variant="default">
                          OAuth Verified
                        </Badge>
                      </div>
                    </div>
                    
                    <div>
                      <label className="text-sm font-medium text-slate-600 dark:text-slate-300">Full Name</label>
                      <p className="text-slate-900 dark:text-white mt-1">
                        {user.firstName && user.lastName 
                          ? `${user.firstName} ${user.lastName}` 
                          : 'Not provided'
                        }
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-600 dark:text-slate-300">Account Role</label>
                      <div className="mt-1">
                        <Badge className={
                          user.role === 'admin' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                          user.role === 'premium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                          'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                        }>
                          {user.role?.charAt(0).toUpperCase() + user.role?.slice(1)}
                        </Badge>
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-slate-600 dark:text-slate-300">Last Login</label>
                      <p className="text-slate-900 dark:text-white mt-1">
                        {user.lastLoginAt 
                          ? new Date(user.lastLoginAt).toLocaleDateString()
                          : 'This session'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Subscription Details */}
            {subscription && (subscription as any).planType && (
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <CreditCard className="w-5 h-5 mr-2" />
                    Subscription Details
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-slate-600 dark:text-slate-300">Plan Type</label>
                        <p className="text-slate-900 dark:text-white mt-1 capitalize">{(subscription as any).planType}</p>
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium text-slate-600 dark:text-slate-300">Status</label>
                        <div className="mt-1">
                          <Badge className={getSubscriptionColor()}>
                            {(subscription as any).status}
                          </Badge>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-slate-600 dark:text-slate-300">Billing Cycle</label>
                        <p className="text-slate-900 dark:text-white mt-1 capitalize">{(subscription as any).billingInterval}ly</p>
                      </div>
                      
                      <div>
                        <label className="text-sm font-medium text-slate-600 dark:text-slate-300">Next Billing Date</label>
                        <p className="text-slate-900 dark:text-white mt-1">
                          {new Date((subscription as any).currentPeriodEnd).toLocaleDateString()}
                        </p>
                      </div>
                    </div>

                    {(subscription as any).trialEnd && (
                      <div>
                        <label className="text-sm font-medium text-slate-600 dark:text-slate-300">Trial Ends</label>
                        <p className="text-slate-900 dark:text-white mt-1">
                          {new Date((subscription as any).trialEnd).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Quick Actions & Stats */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle>Quick Stats</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600 dark:text-slate-300">Account Age</span>
                    <span className="font-medium">
                      {user.createdAt 
                        ? `${Math.floor((Date.now() - new Date(user.createdAt).getTime()) / (1000 * 60 * 60 * 24))} days`
                        : 'N/A'
                      }
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600 dark:text-slate-300">Profile Completion</span>
                    <span className="font-medium">
                      {(() => {
                        let completion = 0;
                        if (user.email) completion += 25;
                        if (user.firstName) completion += 25;
                        if (user.lastName) completion += 25;
                        if (user.isEmailVerified) completion += 25;
                        return `${completion}%`;
                      })()}
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600 dark:text-slate-300">Account Security</span>
                    <Badge variant="default">
                      OAuth Secured
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Activity Card */}
            <Card className="mt-6">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Activity className="w-5 h-5 mr-2" />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                    <div>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">Account created</p>
                      <p className="text-xs text-slate-600 dark:text-slate-300">
                        {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Unknown'}
                      </p>
                    </div>
                  </div>
                  
                  {user.lastLoginAt && (
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                      <div>
                        <p className="text-sm font-medium text-slate-900 dark:text-white">Last login</p>
                        <p className="text-xs text-slate-600 dark:text-slate-300">
                          {new Date(user.lastLoginAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  )}

                  {subscription && ((subscription as any).createdAt || (subscription as any).currentPeriodStart) && (
                    <div className="flex items-start space-x-3">
                      <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
                      <div>
                        <p className="text-sm font-medium text-slate-900 dark:text-white">Subscription started</p>
                        <p className="text-xs text-slate-600 dark:text-slate-300">
                          {new Date((subscription as any).createdAt || (subscription as any).currentPeriodStart).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
